/**
 * 4-axis radar for assessment dimension scores. Always 4 dimensions, fixed
 * order: basics / preflop / flop / turnRiver. Values are 0-100.
 *
 * Pure SVG — no chart lib pulled in. Self-contained sizing so callers
 * don't need to wire viewBox themselves; the inline width is set via
 * className from outside.
 */
import { Fragment } from 'react';
import type { Dimension } from '@shared/types/question.js';
import { DIMENSION_LABELS } from '@shared/config/dimensions.js';

export type RadarDimension = Dimension;

// 标签直接取自 shared DIMENSION_LABELS (单一真相源); AXES 只负责定义雷达轴的顺序.
const AXES: { key: RadarDimension; label: string }[] = [
  { key: 'preflop', label: DIMENSION_LABELS.preflop },
  { key: 'flop', label: DIMENSION_LABELS.flop },
  { key: 'turnRiver', label: DIMENSION_LABELS.turnRiver },
  { key: 'basics', label: DIMENSION_LABELS.basics },
];

interface Props {
  values: Record<RadarDimension, number>; // each 0-100
  size?: number;
  showLabels?: boolean;
}

export function RadarChart({ values, size = 220, showLabels = true }: Props): JSX.Element {
  const cx = size / 2;
  const cy = size / 2;
  const radius = size * 0.36;
  const labelOffset = radius + 22;

  // viewBox 留出 label 外边距 — fontSize 11 下最长标签「转河复杂处理」6 字
  // 宽 ≈ 66px, 加 labelOffset 22 + 安全余量, 50px 内边距足够. 不留的话左右
  // label 会被 SVG viewBox 切掉 (历史 bug, 2026/05 修).
  const labelPadding = showLabels ? 50 : 0;
  const viewBoxSize = size + labelPadding * 2;

  // Empty state: 所有维度都是 0 (异常数据) 时画不出多边形, 显示提示.
  const allZero =
    showLabels && AXES.every((axis) => (values[axis.key] ?? 0) <= 0);

  // Axis points around the circle, starting at top, clockwise.
  const angle = (i: number): number => (-Math.PI / 2) + (i * 2 * Math.PI) / AXES.length;
  const point = (i: number, r: number): [number, number] => [
    cx + Math.cos(angle(i)) * r,
    cy + Math.sin(angle(i)) * r,
  ];

  // Web (data) polygon.
  const dataPts = AXES.map((axis, i) => {
    const v = Math.max(0, Math.min(100, values[axis.key] ?? 0));
    return point(i, (radius * v) / 100);
  });
  const dataPath = dataPts.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');

  // Gridlines at 25 / 50 / 75 / 100.
  const grid = [0.25, 0.5, 0.75, 1.0];

  if (allZero) {
    return (
      <div
        className="w-full flex items-center justify-center text-xs text-slate-500"
        style={{ aspectRatio: '1 / 1', maxWidth: size }}
        role="img"
        aria-label="能力雷达图（暂无维度数据）"
      >
        暂无维度数据
      </div>
    );
  }

  return (
    <svg
      viewBox={`-${labelPadding} -${labelPadding} ${viewBoxSize} ${viewBoxSize}`}
      className="w-full h-auto"
      role="img"
      aria-label="能力雷达图"
    >
      {/* Grid rings */}
      {grid.map((g, idx) => {
        const ringPts = AXES.map((_, i) => point(i, radius * g));
        const d = ringPts.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
        return (
          <polygon
            key={idx}
            points={d}
            fill="none"
            stroke="rgb(51 65 85)"
            strokeWidth={idx === grid.length - 1 ? 1.5 : 1}
            strokeOpacity={idx === grid.length - 1 ? 1 : 0.5}
          />
        );
      })}

      {/* Axes spokes */}
      {AXES.map((_, i) => {
        const [x, y] = point(i, radius);
        return (
          <line
            key={i}
            x1={cx}
            y1={cy}
            x2={x}
            y2={y}
            stroke="rgb(51 65 85)"
            strokeWidth={1}
            strokeOpacity={0.4}
          />
        );
      })}

      {/* Data polygon */}
      <polygon
        points={dataPath}
        fill="rgb(56 189 248 / 0.25)"
        stroke="rgb(56 189 248)"
        strokeWidth={2}
        strokeLinejoin="round"
      />
      {dataPts.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r={3} fill="rgb(56 189 248)" />
      ))}

      {/* Axis labels + score */}
      {showLabels && AXES.map((axis, i) => {
        const [lx, ly] = point(i, labelOffset);
        const v = Math.max(0, Math.min(100, values[axis.key] ?? 0));
        const anchor =
          Math.abs(lx - cx) < 1 ? 'middle' : lx > cx ? 'start' : 'end';
        return (
          <Fragment key={axis.key}>
            <text
              x={lx}
              y={ly}
              fontSize={11}
              fill="rgb(148 163 184)"
              textAnchor={anchor}
              dominantBaseline="middle"
            >
              {axis.label}
            </text>
            <text
              x={lx}
              y={ly + 12}
              fontSize={10}
              fill="rgb(56 189 248)"
              fontWeight="600"
              textAnchor={anchor}
              dominantBaseline="middle"
            >
              {v.toFixed(0)}
            </text>
          </Fragment>
        );
      })}
    </svg>
  );
}
